/**
 * useOverlayBackDismiss — make the device/browser Back button DISMISS an open
 * overlay (mobile chat Sheet) instead of navigating away from the page.
 *
 * Why: on mobile the chat panel is a full-screen overlay. The expected gesture
 * to leave it is Back — but a bare Back would change route and unmount the
 * embedding (tearing down the observer WebSocket and dropping the in-flight
 * turn). Instead we push a sentinel history entry while the overlay is open and
 * translate the resulting `popstate` into `onDismiss()` — the same collapse path
 * as the X button, which keeps the chat session bound (the WS survives because
 * `useChatSession` lives outside the Sheet's collapsible content).
 *
 * Mirrors the history-sentinel pattern in useUnsavedChangesWarning. Like that
 * hook, it carries a known, accepted edge: a programmatic close→reopen within
 * the same microtask could let the cleanup's async history.back() pop reach the
 * freshly-armed listener. Not reachable here — `open` is user-tap / auto-open
 * driven, never toggled false→true synchronously.
 */
import { useEffect, useId, useRef } from 'react';

interface OverlayBackDismissOptions {
  /** Whether the overlay is currently open. */
  open: boolean;
  /** Called when Back is pressed while open — should collapse, not unmount. */
  onDismiss: () => void;
  /** Only arm on surfaces where Back-to-dismiss applies (e.g. mobile). Default true. */
  enabled?: boolean;
}

export function useOverlayBackDismiss({ open, onDismiss, enabled = true }: OverlayBackDismissOptions): void {
  // Keep the latest callback without re-arming the effect on every render.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Stable marker unique to this hook instance so two overlays open at once
  // (e.g. Luma panel + agent panel) never pop each other's sentinel entry.
  const instanceId = useId();
  const marker = `__chatOverlayBack_${instanceId}`;

  useEffect(() => {
    if (!enabled || !open) return;

    // Push a sentinel entry so Back pops THIS rather than leaving the page.
    window.history.pushState({ [marker]: true }, '');
    let poppedByBack = false;

    const onPop = () => {
      poppedByBack = true;
      onDismissRef.current();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed by something other than Back (X / Escape / programmatic): unwind
      // our sentinel so the page's Back button keeps behaving normally after the
      // overlay is dismissed. Guarded so we only ever pop our own entry.
      if (!poppedByBack && (window.history.state as Record<string, unknown> | null)?.[marker]) {
        window.history.back();
      }
    };
  }, [open, enabled, marker]);
}
